import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";

export default function NewPlanDialog({
  open,
  onOpenChange,
  onCreate,
  existingIds = [],
}) {
  const [form, setForm] = useState({ id: "", initials: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ id: "", initials: "" });
      setError("");
    }
  }, [open]);

  const update = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const id = form.id.trim();
    const initials = form.initials.trim().toUpperCase();
    if (!id) {
      setError("Patient ID is required.");
      return;
    }
    if (!initials) {
      setError("Initials are required.");
      return;
    }
    if (initials.length < 2 || initials.length > 4) {
      setError("Initials should be 2–4 letters.");
      return;
    }
    const existing = existingIds.find((x) => x.toLowerCase() === id.toLowerCase());
    onCreate({ id: existing || id, initials, isExisting: !!existing });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="new-plan-dialog"
        className="sm:max-w-[440px] bg-white rounded-3xl border-slate-200"
      >
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl font-bold tracking-tight">
            Start a new plan
          </DialogTitle>
          <p className="font-figtree text-sm text-[#64748B] leading-relaxed">
            We only store the Patient ID from your main system and the patient's
            initials. No names, no PHI.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <Label
              htmlFor="patient-id"
              className="font-figtree text-xs font-medium text-[#475569]"
            >
              Patient ID
            </Label>
            <Input
              id="patient-id"
              data-testid="new-plan-id"
              value={form.id}
              onChange={(e) => update("id", e.target.value)}
              placeholder="e.g., PT-1042"
              required
              autoFocus
              className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-[#10B981] font-figtree"
            />
            <p className="mt-1.5 font-figtree text-[11px] text-[#94A3B8]">
              The ID from your central system. We never see the patient's name.
            </p>
          </div>

          <div>
            <Label
              htmlFor="patient-initials"
              className="font-figtree text-xs font-medium text-[#475569]"
            >
              Patient initials
            </Label>
            <Input
              id="patient-initials"
              data-testid="new-plan-initials"
              value={form.initials}
              onChange={(e) => update("initials", e.target.value)}
              maxLength={4}
              placeholder="e.g., MK"
              required
              className="mt-1.5 rounded-xl border-slate-200 focus-visible:ring-[#10B981] font-figtree uppercase"
            />
          </div>

          {error && (
            <div
              data-testid="new-plan-error"
              className="font-figtree text-sm text-[#DC2626]"
            >
              {error}
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid="new-plan-cancel-btn"
              className="rounded-full font-figtree"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="new-plan-create-btn"
              className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white font-figtree font-medium"
            >
              Start plan
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
